import { z } from "zod";
import { addressStrictSchema } from "./address";
import { defaultDateString } from "./shared";

export enum EhrSources {
  athena = "athenahealth",
  elation = "elation",
  canvas = "canvas",
  healthie = "healthie",
  eclinicalworks = "eclinicalworks",
  salesforce = "salesforce",
  practicefusion = "practicefusion",
}

export const patientBasicInfoSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
});
export type PatientBasicInfo = z.infer<typeof patientBasicInfoSchema>;

const coverageAssessmentPatientSchema = z.object({
  dob: z.string().optional(),
  gender: z.string().optional(),
  firstname: z.string().optional(),
  lastname: z.string().optional(),
  zip: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  addressline1: z.string().optional(),
  addressline2: z.string().optional(),
  phone1: z.string().optional(),
  phone2: z.string().optional(),
  email1: z.string().optional(),
  email2: z.string().optional(),
  externalid: z.string().optional(),
});

const coverageAssessmentSchema = z.object({
  patients: coverageAssessmentPatientSchema.array(),
});
export type CoverageAssessment = z.infer<typeof coverageAssessmentSchema>;

const patientCoverageSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  dob: z.string(),
  address: addressStrictSchema.array(),
  downloadStatus: z.string().optional(),
  docCount: z.number().optional(),
  convertStatus: z.string().optional(),
  fhirCount: z.number(),
  fhirDetails: z.string(),
  mrSummaryUrl: z.string().optional(),
});
export type PatientCoverage = z.infer<typeof patientCoverageSchema>;

export const patientCoverageResponseSchema = z.object({
  patientsWithAssessments: patientCoverageSchema.array(),
});
export type PatientCoverageResponse = z.infer<typeof patientCoverageResponseSchema>;

export const bulkPatientImportStatusSchema = z.enum([
  "waiting",
  "processing",
  "completed",
  "failed",
]);

export const patientImportParamsCxSchema = z.object({
  dryRun: z.boolean().nullish(),
});

export const patientImportParamsOpsSchema = z.object({
  dryRun: z.boolean().nullish(),
  rerunPdOnNewDemographics: z.boolean(),
  triggerConsolidated: z.boolean(),
  disableWebhooks: z.boolean(),
});

export const bulkPatientImportSchema = z.object({
  id: z.string(),
  cxId: z.string(),
  facilityId: z.string(),
  status: bulkPatientImportStatusSchema,
  reason: z.string().nullish(),
  createdAt: defaultDateString,
  startedAt: defaultDateString.nullish(),
  finishedAt: defaultDateString.nullish(),
  total: z.number(),
  successful: z.number(),
  failed: z.number(),
  paramsCx: patientImportParamsCxSchema,
  paramsOps: patientImportParamsOpsSchema,
  validEntriesUrl: z.string().nullish(),
  invalidEntriesUrl: z.string().nullish(),
});

export const bulkPatientImportCreateSchema = z.object({
  cxId: z.string(),
  facilityId: z.string().optional(),
  dryRun: z.boolean(),
});

export type BulkPatientImportCreate = z.infer<typeof bulkPatientImportCreateSchema>;

export const bulkPatientImportCreateResponseSchema = bulkPatientImportSchema.merge(
  z.object({
    id: z.string(),
    uploadUrl: z.string(),
  })
);

export const bulkPatientImportResponseSchema = z.array(bulkPatientImportSchema);

export type BulkPatientImportStatus = z.infer<typeof bulkPatientImportStatusSchema>;
export type PatientImportParamsCx = z.infer<typeof patientImportParamsCxSchema>;
export type PatientImportParamsOps = z.infer<typeof patientImportParamsOpsSchema>;
export type BulkPatientImport = z.infer<typeof bulkPatientImportSchema>;
export type BulkPatientImportCreateResponse = z.infer<typeof bulkPatientImportCreateResponseSchema>;
export type BulkPatientImportResponse = z.infer<typeof bulkPatientImportResponseSchema>;
